import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Reveal from "@/components/motion/Reveal";
import KineticLines from "@/components/motion/KineticLines";
import { submitBooking, fetchPackages } from "@/lib/api";
import { Mushroom, Snake, Leaf, Bat, Rose, Candelabra } from "@/components/decor/SvgIcons";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

const initial = {
  name: "",
  organization: "",
  email: "",
  phone: "",
  event_date: "",
  location: "",
  attendance: "",
  indoor_outdoor: "Indoor",
  package: "Curious Critters",
  questions: "",
};

export default function Booking() {
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchPackages().then(setPackages).catch(() => {});
  }, []);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitBooking(form);
      setSuccess(true);
      toast.success("Your request is on its way to the cottage!");
      setForm(initial);
    } catch (err) {
      toast.error("Something rustled in the leaves — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-28">
      {/* Header */}
      <section className="relative overflow-hidden pb-14" data-testid="booking-hero">
        <div className="absolute -top-4 right-8 opacity-70"><Snake size={90} className="rotate-12" /></div>
        <div className="absolute top-40 left-4 opacity-50"><Leaf size={60} className="rotate-45" /></div>
        <div className="absolute top-12 right-1/3 opacity-80 hidden md:block"><Bat size={54} /></div>
        <div className="absolute top-56 left-1/3 opacity-70 hidden md:block"><Rose size={60} /></div>

        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="chip mb-6">Presentations · Booking</div>
          <KineticLines
            lines={["Pick your", "adventure."]}
            italicIdx={1}
            className="font-display text-forest text-[13vw] md:text-[7.5vw] lg:text-[6.5rem] leading-[0.95] tracking-tight"
          />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-lg text-forest/80">
              Four thoughtfully-crafted programs for every kind of gathering — from twenty
              curious three-year-olds to a thousand festival guests.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Packages (numbered manifesto) */}
      <section className="py-16 md:py-24" data-testid="booking-packages">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 space-y-10">
          {packages.map((p, i) => (
            <Reveal key={p.id} delay={0.05 * i}>
              <div className="grim-card grid md:grid-cols-12 gap-6 p-8 md:p-10 relative overflow-hidden">
                <div className="chapter-num text-[12rem] md:text-[16rem] absolute -top-10 -right-4 select-none pointer-events-none">
                  0{i + 1}
                </div>
                <div className="md:col-span-4">
                  <div className="text-xs tracking-[0.22em] uppercase text-forest/60">Chapter · 0{i + 1}</div>
                  <h3 className="font-display text-4xl md:text-5xl text-forest mt-2 leading-[1.02]">{p.name}</h3>
                  <div className="font-script text-2xl text-plum mt-2">{p.duration}</div>
                </div>
                <div className="md:col-span-5">
                  <p className="text-forest/80 text-lg italic">{p.tagline}</p>
                  <div className="mt-5">
                    <div className="text-xs uppercase tracking-widest text-forest/60 mb-2">Ideal for</div>
                    <div className="flex flex-wrap gap-2">
                      {p.ideal_for.map((v) => (
                        <span key={v} className="chip !text-[10px]">{v}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="md:col-span-3">
                  <div className="text-xs uppercase tracking-widest text-forest/60 mb-2">Included</div>
                  <ul className="space-y-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-forest/85">
                        <CheckCircle size={18} weight="duotone" className="mt-0.5 shrink-0 text-plum" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-20 md:py-28 relative" data-testid="booking-form-section">
        <div className="absolute inset-x-0 top-0 h-full -z-10">
          <div className="absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-lavender/20 blur-3xl" />
          <div className="absolute bottom-10 right-0 w-[420px] h-[420px] rounded-full bg-plum/15 blur-3xl" />
        </div>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="chip mb-4">Send a raven</div>
              <h2 className="font-display text-5xl md:text-6xl text-forest leading-[1.02]">
                Request a <em className="text-plum">booking.</em>
              </h2>
              <p className="mt-5 text-forest/80 text-lg">
                Share a few details and we'll reply within two business days with availability,
                a quote, and a warm hello from the cottage.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <Candelabra size={64} />
                <div className="font-script text-2xl text-plum">every event is a story.</div>
              </div>
              <div className="absolute right-8 hidden lg:block"><Snake size={80} /></div>
            </div>

            <div className="lg:col-span-8">
              {success ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grim-card p-10 text-center"
                  data-testid="booking-success"
                >
                  <div className="mx-auto w-16 h-16 rounded-full bg-sage/40 flex items-center justify-center">
                    <Snake size={30} />
                  </div>
                  <h3 className="font-display text-4xl text-forest mt-5">Thank you!</h3>
                  <p className="mt-3 text-forest/80 max-w-md mx-auto">
                    Your booking request has been safely delivered by moth-post. We'll be in touch soon.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="btn-pill btn-secondary mt-6"
                    data-testid="booking-reset"
                  >
                    Send another request
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={onSubmit} className="grim-card p-6 md:p-10 space-y-5" data-testid="booking-form">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/70">Your name</label>
                      <input required value={form.name} onChange={change("name")} className="input-woodland mt-1" data-testid="booking-input-name" placeholder="Wren Ashwood" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/70">Organization</label>
                      <input value={form.organization} onChange={change("organization")} className="input-woodland mt-1" data-testid="booking-input-org" placeholder="Moss Grove Elementary" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/70">Email</label>
                      <input required type="email" value={form.email} onChange={change("email")} className="input-woodland mt-1" data-testid="booking-input-email" placeholder="wren@example.com" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/70">Phone</label>
                      <input value={form.phone} onChange={change("phone")} className="input-woodland mt-1" data-testid="booking-input-phone" placeholder="(555) 555 – 0110" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/70">Event date</label>
                      <input required type="date" value={form.event_date} onChange={change("event_date")} className="input-woodland mt-1" data-testid="booking-input-date" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/70">Location</label>
                      <input required value={form.location} onChange={change("location")} className="input-woodland mt-1" data-testid="booking-input-location" placeholder="Salem, MA" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/70">Estimated attendance</label>
                      <input required value={form.attendance} onChange={change("attendance")} className="input-woodland mt-1" data-testid="booking-input-attendance" placeholder="30 kids + 6 adults" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/70">Setting</label>
                      <select value={form.indoor_outdoor} onChange={change("indoor_outdoor")} className="input-woodland mt-1" data-testid="booking-select-setting">
                        <option>Indoor</option>
                        <option>Outdoor</option>
                        <option>Both / Not sure</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-widest text-forest/70">Preferred package</label>
                      <select value={form.package} onChange={change("package")} className="input-woodland mt-1" data-testid="booking-select-package">
                        {(packages.length ? packages.map((p) => p.name) : ["Tiny Tails", "Curious Critters", "Wild Wonders", "Custom Events"]).map((n) => (
                          <option key={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-widest text-forest/70">Questions or requests</label>
                      <textarea rows={4} value={form.questions} onChange={change("questions")} className="input-woodland mt-1" data-testid="booking-input-questions" placeholder="Tell us about the vibe you're dreaming of…" />
                    </div>
                  </div>

                  <button type="submit" disabled={submitting} className="btn-pill btn-primary w-full justify-center mt-4" data-testid="booking-submit">
                    {submitting ? "Sending…" : "Request Booking"} <ArrowRight size={20} weight="bold" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
